import React, { useContext, useEffect, useState, FC, SyntheticEvent, PropsWithChildren } from 'react';

import { mqMin, BREAKPOINT } from '@lucid/theme';
import { isWindowAboveWidth, isBrowser, useSiteMetadata, getI18nData } from '@lucid/utils';

import { ABTesting, LucidTheme, CustomDrupal, I18n } from '../../types/types';
import ABTest from '../../components/ab-test/ab-test';
import UniversalLoginButton from '../ab-tests/universal-login-button/universal-login-button';
// Needed when reverting ABTest
// import Box from '../box/box';
import Nav from './nav';
import NavDropdown from './nav-dropdown';
import NavLink from './nav-link';
import NavMenuIcon, { NavMenuIconOptions } from './nav-menu-icon';
// Needed when reverting ABTest
// import LanguageSwitcher from '../language-switcher/language-switcher';
import LayoutContext from '../context/layout-context';

export interface NavMenuOptions extends NavMenuIconOptions {
  /** The entity values to allow for customization of urls. */
  entityTranslations?: CustomDrupal.ExtendedDrupalNode[];
  /** Checks if the Sign Up button should be hidden. */
  shouldHideSignUpButton?: boolean;
  /** Checks if menu should be excluded from Nav AB Test */
  shouldExcludeFromNavAbTest?: boolean;
}

export interface NavMenuProps extends NavMenuOptions {
  /** The aria label for screen readers */
  buttonAriaLabel: string;
  /** The navigation buttons that appear at the end of the menu. */
  buttons: CustomDrupal.LinkInterface[];
  /** Allows the component to get additional styles. */
  className?: string;
  /** The Language Switcher Disabled Languages. */
  disabledLanguages?: I18n.AvailableLanguages[];
  /** The entity values to allow for customization of urls. */
  entityTranslations?: CustomDrupal.ExtendedDrupalNode[];
  /** [For AB Test] - allows to optionally add Login button to lucid-co */
  extraButtons?: CustomDrupal.LinkInterface[];
  /** The i18n translation data. */
  i18nData: I18n.DataEdges;
  /** The navigation menu items to navigate across the site. */
  items: CustomDrupal.LinkInterface[];
  /** The navigation buttons to be used in SSR for intial render. */
  placeholderButtons?: CustomDrupal.LinkInterface[];
  /** The navigation links to be used in SSR for intial render. */
  placeholderLinks?: CustomDrupal.LinkInterface[];
}

const NavMenu: FC<PropsWithChildren<NavMenuProps>> = ({
  buttonAriaLabel,
  buttons,
  className,
  // disabledLanguages = ['da'], // Uncomment when ABTest is complete
  entityTranslations,
  extraButtons,
  isTopNav = false,
  i18nData,
  items,
  placeholderButtons,
  placeholderLinks,
  shouldExcludeFromNavAbTest = false,
  shouldHideSignUpButton = false,
  ...rest
}) => {
  const {
    language: { shortLangPrefix, i18nKey },
  } = useContext(LayoutContext);
  const { product } = useSiteMetadata();
  const { isMenuOpen, setIsMenuOpen } = useContext(Nav.Context);
  const { setActiveMenuCallback } = useContext(Nav.SiteContext);
  const [navButtons, setNavButtons] = useState(placeholderButtons || placeholderLinks || []);
  const i18n = getI18nData(i18nData, i18nKey);
  const navMenuIconLabel = isTopNav ? i18n('mktg-nav-mobile-toggle-suite') : i18n('mktg-nav-mobile-toggle');

  useEffect(() => {
    if (isBrowser()) {
      setNavButtons(buttons);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    if (shouldHideSignUpButton) {
      const currentPlaceholderButtons = navButtons.slice(0, 1);
      setNavButtons(currentPlaceholderButtons);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [shouldHideSignUpButton]);

  const handleOnClick = (e: SyntheticEvent) => {
    if (!isWindowAboveWidth(BREAKPOINT.TABLET_LANDSCAPE)) {
      e.preventDefault();
      if (!isMenuOpen) {
        setIsMenuOpen(true);
        setActiveMenuCallback([setIsMenuOpen]);
      } else {
        setIsMenuOpen(false);
      }
    }
  };

  // [For AB Test] - Conditionally add login button
  const updatedTopButtons: CustomDrupal.LinkInterface[] =
    product === 'co' || isTopNav ? navButtons.concat(extraButtons || []) : navButtons;

  return (
    <>
      <NavMenuIcon aria-label={navMenuIconLabel} isMenuOpen={isMenuOpen} isTopNav={isTopNav} onClick={handleOnClick} />
      <ABTest // Replace with Box component after AB Test has run
        {...rest}
        branchesToRender={[ABTesting.Variation.A, ABTesting.Variation.B]}
        css={(theme: LucidTheme) => [
          {
            label: 'navMenuContainer',
            display: 'flex',
            flexDirection: 'column',
            zIndex: theme.zIndices.zNegative1,
            transform: 'translateY(-100%)',
            transition: 'z-index 0.5s step-start, transform 0.5s ease',
            position: 'absolute',
            top: 0,
            right: 0,
            left: 0,
            backgroundColor: theme.colors.white,
            borderRadius: theme.radii.base,
            boxShadow: theme.shadows.nav,
            [mqMin.tabletSmall]: {
              margin: '0 1.875rem',
            },
            [mqMin.tablet]: {
              margin: '0 3.75rem',
            },
            [mqMin.tabletLandscape]: {
              zIndex: 'auto',
              transform: 'unset',
              position: 'unset',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: 'unset',
              width: '100%',
              boxShadow: 'unset',
              marginRight: 0,
              marginLeft: theme.space.s16,
            },
          },
          isTopNav && {
            [mqMin.tabletLandscape]: {
              backgroundColor: theme.colors.coolGrey[5],
            },
          },
          isMenuOpen && {
            transform: 'translateY(70px)',
            transition: 'z-index 0.5s step-end, transform 0.5s ease',
          },
        ]}
        // eslint-disable-next-line react/jsx-sort-props
        className={className}
        hasConditionalTrigger={shortLangPrefix === 'en' && !shouldExcludeFromNavAbTest}
        test="Y22Q1_UniversalLoginButton"
      >
        <ul
          css={(theme: LucidTheme) => ({
            label: 'navMenuLinkUL',
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            margin: 0,
            marginBlock: 0,
            paddingInline: 0,
            width: '100%',
            '& > li > a, & > li > button': {
              borderBottom: `1px solid ${theme.colors.coolGrey[40]}`,
            },
            '& > li:first-of-type': {
              borderTopRightRadius: theme.radii.base,
              borderTopLeftRadius: theme.radii.base,
              '& > button': {
                borderTopRightRadius: theme.radii.base,
                borderTopLeftRadius: theme.radii.base,
                '&:hover': {
                  borderTopRightRadius: theme.radii.base,
                  borderTopLeftRadius: theme.radii.base,
                },
              },
            },
            borderTop: `1px solid ${theme.colors.coolGrey[40]}`,
            borderRight: `1px solid ${theme.colors.coolGrey[40]}`,
            borderLeft: `1px solid ${theme.colors.coolGrey[40]}`,
            borderTopRightRadius: theme.radii.base,
            borderTopLeftRadius: theme.radii.base,
            [mqMin.tabletLandscape]: {
              height: '100%',
              flexDirection: 'row',
              border: 'unset',
              borderRadius: 'unset',
              '& > li > a, & > li > button': {
                borderBottom: 'unset',
              },
              '& > li:first-of-type': {
                borderRadius: 'unset',
                '& > button': {
                  borderRadius: 'unset',
                  '&:hover': {
                    borderRadius: 'unset',
                  },
                },
              },
            },
          })}
        >
          {items.map((itemLink, index) => {
            const isDropdown = itemLink && !!itemLink?.links?.length;

            return (
              <li
                key={`navMenuLink-${itemLink.entityId}-${index + 1}`}
                css={(theme: LucidTheme) => [
                  {
                    label: 'navMenuLinkLi',
                    width: '100%',
                    [mqMin.tabletLandscape]: {
                      width: 'unset',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      '& > a:not([type=button])': {
                        padding: `0 ${theme.space.s8}px`,
                      },
                    },
                  },
                ]}
              >
                {isDropdown ? (
                  <NavDropdown isTopNav={isTopNav} link={itemLink} />
                ) : (
                  <NavLink isTopNav={isTopNav} link={itemLink} product={product} />
                )}
              </li>
            );
          })}
        </ul>
        {/* Below would replace UniversalLoginButton components in the event we restore layout preceding AB Test */}
        {/* <ul
          css={(theme: LucidTheme) => ({
            label: 'navMenuButtonUL',
            listStyle: 'none',
            display: 'flex',
            alignItems: 'flex-end',
            '& > li:first-of-type': {
              '& > a:not([type=button])': {
                padding: 0,
                textAlign: 'center',
              },
            },
            '& > li:last-of-type': {
              width: '100%',
              [mqMin.tabletSmall]: {
                maxWidth: 'fit-content',
                alignSelf: 'flex-start',
              },
              '& > a[type=button]': {
                justifyContent: 'center',
              },
            },
            flexDirection: 'column',
            margin: 0,
            marginBlock: 0,
            paddingInline: 0,
            width: '100%',
            padding: `0 ${theme.space.s32}px`,
            borderRight: `1px solid ${theme.colors.coolGrey[40]}`,
            borderBottom: `1px solid ${theme.colors.coolGrey[40]}`,
            borderLeft: `1px solid ${theme.colors.coolGrey[40]}`,
            borderBottomRightRadius: theme.radii.base,
            borderBottomLeftRadius: theme.radii.base,
            [mqMin.tabletSmall]: {
              flexDirection: 'row',
              justifyContent: 'flex-end',
            },
            [mqMin.tabletLandscape]: {
              marginBlockStart: 0,
              marginBlockEnd: 0,
              paddingInlineStart: 0,
              height: '100%',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              border: 'unset',
              borderRadius: 'unset',
              padding: 'unset',
              '& > li:first-of-type': {
                '& > a:not([type=button])': {
                  padding: 0,
                  textAlign: 'center',
                  maxWidth: theme.space.s104,
                },
              },
            },
            [mqMin.computer]: {
              '& > li:first-of-type': {
                '& > a:not([type=button])': {
                  padding: 0,
                  textAlign: 'center',
                  maxWidth: 'unset',
                },
              },
            },
          })}
        >
          {isTopNav && (
            <LanguageSwitcher
              buttonAriaLabel={buttonAriaLabel}
              css={(theme: LucidTheme) => ({
                display: 'flex',
                height: 'inherit',
                order: 2,
                alignSelf: 'center',
                marginRight: 0,
                paddingTop: theme.space.s24,
                paddingBottom: theme.space.s24,
                [mqMin.tabletSmall]: {
                  order: 1,
                },
              })}
              disabledLangauges={disabledLanguages}
              entityTranslations={entityTranslations!}
              popupDirection="bottom"
              shortLangPrefix="en"
            />
          )}
          {navButtons.map((buttonLink, index) => (
            <li
              key={`navMenuButton-${buttonLink.entityId}-${index + 1}`}
              css={(theme: LucidTheme) => [
                {
                  label: 'navMenuButtonLi',
                  order: 1,
                  width: 'inherit',
                  '&:first-of-type>a:not([type=button])': {
                    justifyContent: 'center',
                  },
                  [mqMin.tabletSmall]: {
                    order: 2,
                    paddingLeft: theme.space.s24,
                    alignSelf: 'center',
                    '&:first-of-type>a:not([type=button])': {
                      justifyContent: 'flex-end',
                    },
                  },
                  [mqMin.tabletLandscape]: {
                    padding: `0 ${theme.space.s8}px 0 ${theme.space.s24}px`,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: 'flex-end',
                  },
                },
              ]}
            >
              <NavLink link={buttonLink} />
            </li>
          ))}
        </ul> */}
        {/* [For AB Test] */}
        <UniversalLoginButton
          branch={ABTesting.Variation.A}
          buttonAriaLabel={buttonAriaLabel}
          buttons={navButtons}
          entityTranslations={entityTranslations}
          isTopNav={isTopNav}
        />
        <UniversalLoginButton
          branch={ABTesting.Variation.B}
          buttonAriaLabel={buttonAriaLabel}
          buttons={updatedTopButtons}
          entityTranslations={entityTranslations}
          isTopNav={isTopNav}
        />
        {/* [End AB Test] */}
      </ABTest>
    </>
  );
};

export { NavMenu };
export default NavMenu;
